import { Icon } from '@progress/kendo-react-common';
import { TreeView, TreeViewExpandChangeEvent } from '@progress/kendo-react-treeview';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CatalogTreeViewDropdownProps, CatalogTreeViewItem } from 'src/types/catalog/Catalog';
import SearchBox from './SearchBox';

const CatalogTreeViewDropdown: FC<CatalogTreeViewDropdownProps> = ({
  data,
  placeholder = 'Select catalog',
  width = '250px',
  onChange,
  value,
  label,
  expandField = 'expanded',
  textField = 'text',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [treeData, setTreeData] = useState<CatalogTreeViewItem[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [selectedItem, setSelectedItem] = useState<CatalogTreeViewItem | null>(value || null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  // Initialize tree data with proper states
  useEffect(() => {
    const initializeTreeData = (items: CatalogTreeViewItem[]): CatalogTreeViewItem[] =>
      items.map((item) => {
        const processedItem = { ...item };

        // Expand all items by default
        if (processedItem.items && processedItem.items.length > 0) {
          processedItem.expanded = true;
          processedItem.items = initializeTreeData(processedItem.items);
        }

        return processedItem;
      });

    const initializedData = initializeTreeData(data);
    setTreeData(initializedData);
  }, [data]);

  // Update selected item when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setSelectedItem(value);
    }
  }, [value]);

  // Scroll to selected item when dropdown opens
  useEffect(() => {
    if (isOpen && selectedItem) {
      // Use setTimeout to ensure the DOM is rendered before scrolling
      setTimeout(() => {
        const element = document.getElementById('catalog-tree-view-dropdown-selected-item');
        if (element) {
          // Find the scrollable container (the overflow-y-auto div)
          const scrollableContainer = element.closest('.overflow-y-auto') as HTMLElement;
          if (scrollableContainer) {
            const containerRect = scrollableContainer.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            const { scrollTop } = scrollableContainer;

            // Calculate the position relative to the scrollable container
            const elementTop = elementRect.top - containerRect.top + scrollTop;
            const elementBottom = elementTop + elementRect.height;
            const containerHeight = scrollableContainer.clientHeight;

            // Add padding to ensure text is not cut off
            const topPadding = 8;
            const bottomPadding = 128; // More padding at bottom to avoid edge positioning

            // Only scroll if element is not fully visible
            if (elementTop < scrollTop + topPadding) {
              // Scroll up to show the element with padding
              scrollableContainer.scrollTo({
                top: elementTop - topPadding,
                behavior: 'smooth',
              });
            } else if (elementBottom > scrollTop + containerHeight - bottomPadding) {
              // Scroll down to show the element with more bottom padding
              scrollableContainer.scrollTo({
                top: elementBottom - containerHeight + bottomPadding,
                behavior: 'smooth',
              });
            }
          } else {
            // Fallback to scrollIntoView if container not found
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }, 50);
    }
  }, [isOpen, selectedItem]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyboardDismiss = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      window.requestAnimationFrame(() => {
        if (!dropdownRef.current?.contains(document.activeElement)) {
          setIsOpen(false);
        }
      });
    };

    document.addEventListener('keydown', handleKeyboardDismiss);

    return () => {
      document.removeEventListener('keydown', handleKeyboardDismiss);
    };
  }, [isOpen]);

  const onExpandChange = useCallback(
    (event: TreeViewExpandChangeEvent) => {
      const updateExpandedState = (
        items: CatalogTreeViewItem[],
        id: string
      ): CatalogTreeViewItem[] =>
        items.map((item) => {
          if (item.id === id) {
            return { ...item, expanded: !item.expanded };
          }
          if (item.items) {
            return { ...item, items: updateExpandedState(item.items, id) };
          }
          return item;
        });

      setTreeData(updateExpandedState(treeData, event.item.id));
    },
    [treeData]
  );

  const filterTreeData = useCallback(
    (items: CatalogTreeViewItem[], search: string): CatalogTreeViewItem[] => {
      if (!search) return items;

      const searchLower = search.toLowerCase();
      const filtered = items
        .map((item) => {
          const matchesText = item.text.toLowerCase().includes(searchLower);
          const filteredChildren = item.items ? filterTreeData(item.items, search) : [];

          if (matchesText || filteredChildren.length > 0) {
            return {
              ...item,
              items: filteredChildren.length > 0 ? filteredChildren : item.items,
              expanded: search ? true : item.expanded,
            };
          }
          return null;
        })
        .filter((item) => item !== null) as CatalogTreeViewItem[];

      return filtered;
    },
    []
  );

  const filteredData = filterTreeData(treeData, searchValue);

  // Find selected item ID for TreeView selected prop (used by mock in tests)
  const findSelectedItemId = useCallback(
    (items: CatalogTreeViewItem[]): string | null => {
      for (const item of items) {
        if (selectedItem && item.catalogId === selectedItem.catalogId && !item.isGroupHeader) {
          return item.id;
        }
        if (item.items) {
          const found = findSelectedItemId(item.items);
          if (found) return found;
        }
      }
      return null;
    },
    [selectedItem]
  );

  const selectedItemId = findSelectedItemId(treeData);

  const displayText = selectedItem ? selectedItem.text : placeholder;

  // Helper function to recursively toggle expanded state of an item and all its children
  const toggleItemExpandedState = useCallback(
    (
      items: CatalogTreeViewItem[],
      targetId: string,
      shouldExpand?: boolean
    ): CatalogTreeViewItem[] =>
      items.map((item) => {
        if (item.id === targetId) {
          const newExpanded = shouldExpand !== undefined ? shouldExpand : !item.expanded;
          // Recursively expand/collapse all children
          const updatedItems = item.items
            ? item.items.map((child) => ({
                ...child,
                expanded: newExpanded,
                items: child.items
                  ? toggleItemExpandedState([child], child.id, newExpanded)[0].items
                  : undefined,
              }))
            : undefined;
          return {
            ...item,
            expanded: newExpanded,
            items: updatedItems,
          };
        }
        if (item.items) {
          return {
            ...item,
            items: toggleItemExpandedState(item.items, targetId, shouldExpand),
          };
        }
        return item;
      }),
    []
  );

  // Handle item selection (used by mock TreeView in tests)
  const handleSelect = useCallback(
    (event: any) => {
      const item = event.item as CatalogTreeViewItem;

      // Handle group header clicks - toggle expand/collapse
      if (item.isGroupHeader) {
        setTreeData((prevData) => toggleItemExpandedState(prevData, item.id));
        return;
      }

      // Don't allow selecting items without catalogId
      if (!item.catalogId) {
        return;
      }

      // Close dropdown
      setIsOpen(false);

      // Clear search value
      setSearchValue('');

      // Call onChange callback
      if (onChange) {
        onChange(item);
      }
    },
    [onChange, toggleItemExpandedState]
  );

  // Handle item click (for real TreeView - onItemClick)
  const handleItemClick = useCallback(
    (event: any) => {
      const item = event.item as CatalogTreeViewItem;

      // Handle group header clicks - toggle expand/collapse
      if (item.isGroupHeader) {
        setTreeData((prevData) => toggleItemExpandedState(prevData, item.id));
        return;
      }

      // Don't allow selecting items without catalogId
      if (!item.catalogId) {
        return;
      }

      // Close dropdown
      setIsOpen(false);

      // Clear search value
      setSearchValue('');

      // Call onChange callback
      if (onChange) {
        onChange(item);
      }
    },
    [onChange, toggleItemExpandedState]
  );

  // Custom item renderer to style group headers
  const itemRender = useCallback(
    (props: any) => {
      const item = props.item as CatalogTreeViewItem;
      const isGroupHeader = item.isGroupHeader || false;
      const isSelectable = !isGroupHeader && !!item.catalogId;
      const isSelected = selectedItem && item.catalogId === selectedItem.catalogId;

      const shouldShowTooltip =
        item.text && item.text.length > 20 && item.catalogId && !isGroupHeader;

      return (
        <span
          data-group-header={isGroupHeader}
          data-selectable={isSelectable}
          id={isSelected ? 'catalog-tree-view-dropdown-selected-item' : undefined}
          {...(shouldShowTooltip
            ? {
                'data-tooltip-id': 'global-tooltip',
                'data-tooltip-content': item.text,
              }
            : {})}
          style={{
            fontWeight: 'normal',
            fontSize: isGroupHeader ? '12px' : '13px',
            color: isSelected ? '#ffffff' : isGroupHeader ? '#777777' : '#333',
            cursor: isSelected ? 'not-allowed' : isGroupHeader ? 'pointer' : 'pointer',
            backgroundColor: isSelected ? '#0E80B4' : 'transparent',
            padding: isSelected ? '4px' : '0',
            pointerEvents: 'auto',
            whiteSpace: 'nowrap',
          }}
        >
          {item.text}
        </span>
      );
    },
    [selectedItem]
  );

  return (
    <div ref={dropdownRef} className="relative catalog-tree-view-dropdown" style={{ width }}>
      {label && <label className="text-xs mb-1 block">{label}</label>}

      {/* Dropdown Toggle - Matching Kendo UI DropDownList Style */}
      <button
        type="button"
        className="k-input k-input-md k-rounded-md k-input-solid k-picker k-picker-md k-picker-solid k-dropdownlist w-full"
        onClick={toggleDropdown}
        style={{
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span
          className="k-input-inner"
          style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          <span
            className="k-input-value-text"
            {...(displayText &&
            displayText.length > 20 &&
            selectedItem &&
            selectedItem.catalogId &&
            !selectedItem.isGroupHeader
              ? {
                  'data-tooltip-id': 'global-tooltip',
                  'data-tooltip-content': displayText,
                }
              : {})}
          >
            {displayText}
          </span>
        </span>
        <span className="k-input-button k-button k-button-md k-rounded-md k-button-solid k-button-solid-base k-icon-button">
          <span className="k-button-icon k-icon">
            <Icon name={isOpen ? 'caret-alt-up' : 'caret-alt-down'} />
          </span>
        </span>
      </button>
      {/* Dropdown Content */}
      {isOpen && (
        <div
          className="k-animation-container k-animation-container-shown absolute inline-dropdown-overlay mt-1"
          style={{ width: '100%' }}
        >
          <div className="k-popup k-group k-reset k-list-container" style={{ maxHeight: '400px' }}>
            {/* Search Box */}
            <div className="p-2 border-b border-gray-200">
              <SearchBox
                placeholder={t('Search')}
                width="100%"
                value={searchValue}
                onChange={setSearchValue}
              />
            </div>

            {/* TreeView */}
            <div className="overflow-x-auto overflow-y-auto">
              <div className="w-fit min-w-full">
                {filteredData.length > 0 ? (
                  <TreeView
                    data={filteredData}
                    expandIcons={true}
                    onExpandChange={onExpandChange}
                    {...({
                      onSelect: handleSelect,
                      selected: selectedItemId || undefined,
                    } as any)}
                    onItemClick={handleItemClick}
                    textField={textField}
                    expandField={expandField}
                    item={itemRender}
                  />
                ) : (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    <Icon name="warning" className="text-2xl mb-2" />
                    <p>NO DATA FOUND.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatalogTreeViewDropdown;
